import React, { createContext, useContext, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise, STRIPE_CONFIG } from '../lib/stripe';
import { supabase } from '../lib/supabaseClient';

interface StripeContextType {
  clientSecret: string | null;
  setClientSecret: (secret: string | null) => void;
  createPaymentIntent: (amount: number, packageId: string) => Promise<string | null>;
  isLoading: boolean;
  error: string | null;
}

const StripeContext = createContext<StripeContextType | undefined>(undefined);

export const useStripe = () => {
  const context = useContext(StripeContext);
  if (context === undefined) {
    console.error('useStripe must be used within a StripeProvider');
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
};

interface StripeProviderProps {
  children: React.ReactNode;
}

export const StripeProvider: React.FC<StripeProviderProps> = ({ children }) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createPaymentIntent = async (amount: number, packageId: string): Promise<string | null> => {
    setIsLoading(true);
    setError(null);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Create payment intent via Supabase Edge Function
      const { data, error: functionError } = await supabase.functions.invoke('create-payment-intent', {
        body: {
          amount: amount,
          currency: 'eur',
          package_id: packageId,
          user_id: user.id,
        },
      });

      if (functionError) {
        throw functionError;
      }

      console.log('Payment intent creation response:', data);

      if (data?.client_secret) {
        setClientSecret(data.client_secret);
        console.log('Client secret set:', data.client_secret);
        return data.client_secret;
      }

      console.error('No client secret in response:', data);
      throw new Error('Failed to create payment intent');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error creating payment intent:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const value: StripeContextType = {
    clientSecret,
    setClientSecret,
    createPaymentIntent,
    isLoading,
    error,
  };

  return (
    <StripeContext.Provider value={value}>
      {clientSecret ? (
        <Elements stripe={stripePromise} options={{ ...STRIPE_CONFIG, clientSecret }}>
          {children}
        </Elements>
      ) : (
        children
      )}
    </StripeContext.Provider>
  );
};
